import { createSelector } from 'reselect';
import get from 'lodash/get';
import difference from 'lodash/difference';

import type { State } from 'services/reducers/index';
import type {
  StateProductData,
  VariantData,
  BundledProduct,
  Favorites,
  CartQuantities,
  CadenceTypes,
} from '../sharedTypes';
import type { WithSelectedProductLinkInjectedProps } from '../HOC/withSelectedProductLink/withSelectedProductLink';

export const productTypeEnum = {
  IN_STOCK: 'IN_STOCK',
  SPONSORED: 'SPONSORED',
  GWP: 'GWP',
  SOLD_OUT: 'SOLD_OUT',
  DEFAULT: 'DEFAULT',
  FEATURED: 'FEATURED',
  ALL: 'ALL',
};
type SelectProductsProps = {
  +type: string,
  +selectedCategoryId: string | null,
};

const mapProductsToGwp = (
  sponsoredProductIds,
  searchResults,
  products,
  variants,
  variantIdsBeingUpdated,
  favorites,
  variantQuantities,
  prevPurchasedVariantQuantities
) => {
  return sponsoredProductIds
    .filter((productId) => !searchResults || searchResults.includes(productId))
    .map((productId) =>
      bundleProduct(
        productId,
        products,
        variants,
        variantIdsBeingUpdated,
        favorites,
        variantQuantities,
        prevPurchasedVariantQuantities,
        productTypeEnum.GWP
      )
    )
    .filter((product) => {
      return product.gwp;
    })
    .sort((a, b) => a.gwpThreshold - b.gwpThreshold);
};

/**
 * Higher order function that returns a selector that can be shared and retain memoization.
 * See https://redux.js.org/recipes/computing-derived-data#sharing-selectors-across-multiple-components
 * @return selector that filters products by type and bundles them with their variants
 */
export function makeSelectProducts(): (
  State,
  SelectProductsProps
) => Array<BundledProduct> {
  return createSelector(
    (state) => state.productsState.products,
    (state) => state.productsState.variants,
    (state) => state.productsState.displayedProductIds,
    (state) => state.productsState.allProductIds,
    (state) => state.productsState.sponsoredProductIds,
    (state) => state.productsState.favorites,
    (state) => state.productsState.subscribableProducts,
    (state) => state.cartState.variantIdsBeingUpdated,
    (state) => state.cartState.variantQuantities,
    (state, props) => props.type,
    (state, props) => props.selectedCategoryId,
    (state) => state.cartState.prevPurchasedVariantQuantities,
    (state) => state.cartState.variantCadenceTypes,
    (state) => state.searchState.searchResults,
    (state) => state,
    (state, props) => props,
    (
      products,
      variants,
      displayedProductIds,
      allProductIds,
      sponsoredProductIds,
      favorites,
      subscribableProducts,
      variantIdsBeingUpdated,
      variantQuantities,
      type,
      selectedCategoryId,
      prevPurchasedVariantQuantities,
      variantCadenceTypes,
      searchResults,
      state,
      props
    ) => {
      const filteredDisplayProductIds = searchResults || displayedProductIds;
      const filteredAllProductIds = difference(
        allProductIds,
        displayedProductIds
      );
      switch (type) {
        case productTypeEnum.IN_STOCK:
          return filteredDisplayProductIds
            .map((productId) =>
              bundleProduct(
                productId,
                products,
                variants,
                variantIdsBeingUpdated,
                favorites,
                variantQuantities,
                prevPurchasedVariantQuantities,
                productTypeEnum.IN_STOCK,
                subscribableProducts,
                variantCadenceTypes
              )
            )
            .filter((product) => {
              const {
                sponsored,
                isSoldOutOnFetch,
                isFavorite,
                isProductAtMaxedCartLimit,
                gwp,
                isSubscribableShopProduct,
                isSubscriptionSoldOutOnFetch,
                hasActiveSubscription,
              } = product;
              // the product is OOS if one-time purchase is sold out + not subscribable
              // OR one-time purchase is sold out + subscribable + subscription is sold out
              // OR one-time purchase is sold out + subscribable + user already has a subscription
              const subscribableSoldOut =
                isSubscribableShopProduct &&
                (isSubscriptionSoldOutOnFetch || hasActiveSubscription);
              const isSoldOut =
                isSoldOutOnFetch &&
                (!isSubscribableShopProduct || subscribableSoldOut);
              if (isProductAtMaxedCartLimit) {
                return false;
              }
              if (!searchResults) {
                if (selectedCategoryId === 'mycart')
                  return (
                    isSomeVariantInCart(product) &&
                    !sponsored &&
                    !isSoldOut &&
                    !gwp
                  );
                if (selectedCategoryId === 'myfavorites')
                  return isFavorite && !isSoldOut;
              }
              return !sponsored && !isSoldOut;
            });
        case productTypeEnum.ALL:
          return filteredAllProductIds
            .map((productId) =>
              bundleProduct(
                productId,
                products,
                variants,
                variantIdsBeingUpdated,
                favorites,
                variantQuantities,
                prevPurchasedVariantQuantities,
                productTypeEnum.IN_STOCK,
                subscribableProducts,
                variantCadenceTypes
              )
            )
            .filter((product) => {
              const {
                sponsored,
                isSoldOutOnFetch,
                isProductAtMaxedCartLimit,
                isSubscribableShopProduct,
                isSubscriptionSoldOutOnFetch,
              } = product;
              const isSoldOut =
                isSoldOutOnFetch &&
                (!isSubscribableShopProduct ||
                  (isSubscribableShopProduct && isSubscriptionSoldOutOnFetch));
              if (isProductAtMaxedCartLimit) {
                return false;
              }
              return !sponsored && !isSoldOut;
            });
        case productTypeEnum.SPONSORED:
          return sponsoredProductIds
            .filter(
              (productId) => !searchResults || searchResults.includes(productId)
            )
            .map((productId) =>
              bundleProduct(
                productId,
                products,
                variants,
                variantIdsBeingUpdated,
                favorites,
                variantQuantities,
                prevPurchasedVariantQuantities,
                productTypeEnum.SPONSORED,
                subscribableProducts,
                variantCadenceTypes
              )
            )
            .filter((product) => {
              if (product.isProductAtMaxedCartLimit) {
                return false;
              }
              return product.sponsored && !product.gwp;
            });
        case productTypeEnum.SOLD_OUT:
          return filteredDisplayProductIds
            .map((productId) =>
              bundleProduct(
                productId,
                products,
                variants,
                variantIdsBeingUpdated,
                favorites,
                variantQuantities,
                prevPurchasedVariantQuantities,
                productTypeEnum.SOLD_OUT,
                subscribableProducts,
                variantCadenceTypes
              )
            )
            .filter((product) => {
              const {
                isSoldOutOnFetch,
                sponsored,
                isFavorite,
                isProductAtMaxedCartLimit,
                isSubscribableShopProduct,
                isSubscriptionSoldOutOnFetch,
                hasActiveSubscription,
              } = product;

              // the product is OOS if one-time purchase is sold out + not subscribable
              // OR one-time purchase is sold out + subscribable + subscription is sold out
              // OR one-time purchase is sold out + subscribable + user already has a subscription
              const subscribableSoldOut =
                isSubscribableShopProduct &&
                (isSubscriptionSoldOutOnFetch || hasActiveSubscription);
              const isSoldOut =
                isSoldOutOnFetch &&
                (!isSubscribableShopProduct || subscribableSoldOut);
              if (isProductAtMaxedCartLimit) {
                return false;
              }
              if (!searchResults) {
                if (selectedCategoryId === 'mycart')
                  return (
                    isSomeVariantInCart(product) && !sponsored && isSoldOut
                  );
                if (selectedCategoryId === 'myfavorites')
                  return isFavorite && isSoldOut;
              }
              return isSoldOut && !sponsored;
            });
        case productTypeEnum.GWP:
          return mapProductsToGwp(
            sponsoredProductIds,
            searchResults,
            products,
            variants,
            variantIdsBeingUpdated,
            favorites,
            variantQuantities,
            prevPurchasedVariantQuantities
          );
        case productTypeEnum.FEATURED: {
          const selectFeaturedProducts = makeSelectFeaturedProducts();
          return selectFeaturedProducts(state, props);
        }
        default:
          return displayedProductIds
            .map((productId) =>
              bundleProduct(
                productId,
                products,
                variants,
                variantIdsBeingUpdated,
                favorites,
                variantQuantities,
                prevPurchasedVariantQuantities,
                productTypeEnum.DEFAULT,
                subscribableProducts,
                variantCadenceTypes
              )
            )
            .filter((product) => {
              const { sponsored, isProductAtMaxedCartLimit } = product;
              if (isProductAtMaxedCartLimit) {
                return false;
              }
              return !sponsored;
            });
      }
    }
  );
}

export function makeSelectFeaturedProducts(): (
  State,
  SelectProductsProps
) => Array<BundledProduct> {
  return createSelector(
    (state) => state.productsState.featuredProducts.products,
    (state) => state.productsState.featuredProducts.variants,
    (state) => state.productsState.featuredProducts.productIds,
    (state) => state.cartState.variantIdsBeingUpdated,
    (state) => state.productsState.favorites,
    (state) => state.productsState.subscribableProducts,
    (state) => state.cartState.variantQuantities,
    (state) => state.cartState.prevPurchasedVariantQuantities,
    (state) => state.cartState.variantCadenceTypes,
    (state) => state.userState.shippingRestriction,
    (state, props) => props.selectedCategoryId,
    (
      products,
      variants,
      productIds,
      variantIdsBeingUpdated,
      favorites,
      subscribableProducts,
      variantQuantities,
      prevPurchasedVariantQuantities,
      variantCadenceTypes,
      shippingRestriction,
      selectedCategoryId
    ) => {
      return productIds
        .map((productId) =>
          bundleProduct(
            productId,
            products,
            variants,
            variantIdsBeingUpdated,
            favorites,
            variantQuantities,
            prevPurchasedVariantQuantities,
            productTypeEnum.IN_STOCK,
            subscribableProducts,
            variantCadenceTypes
          )
        )
        .filter((product) => {
          const {
            sponsored,
            soldOut,
            isProductAtMaxedCartLimit,
            shippingRestrictions,
            isFavorite,
          } = product;
          if (selectedCategoryId === 'mycart')
            return isSomeVariantInCart(product) && !soldOut;
          if (selectedCategoryId === 'myfavorites') return isFavorite;
          return (
            !sponsored &&
            !soldOut &&
            !shippingRestrictions.includes(shippingRestriction) &&
            !isProductAtMaxedCartLimit
          );
        });
    }
  );
}

export function makeSelectGwpProducts(): (State) => Array<BundledProduct> {
  return createSelector(
    (state) => state.productsState.products,
    (state) => state.productsState.variants,
    (state) => state.productsState.sponsoredProductIds,
    (state) => state.cartState.variantIdsBeingUpdated,
    (state) => state.productsState.favorites,
    (state) => state.cartState.variantQuantities,
    (state) => state.cartState.prevPurchasedVariantQuantities,
    (state) => state.searchState.searchResults,
    (
      products,
      variants,
      sponsoredProductIds,
      variantIdsBeingUpdated,
      favorites,
      variantQuantities,
      prevPurchasedVariantQuantities,
      searchResults
    ) => {
      return mapProductsToGwp(
        sponsoredProductIds,
        searchResults,
        products,
        variants,
        variantIdsBeingUpdated,
        favorites,
        variantQuantities,
        prevPurchasedVariantQuantities
      );
    }
  );
}

type GetSelectedProductIdProps = { +selectedProductLink: string | null };

const getSelectedProductId: (
  State,
  GetSelectedProductIdProps
) => string | null = createSelector(
  (state: State, props: GetSelectedProductIdProps) => props.selectedProductLink,
  (state: State) => state.productsState.products,
  (state: State) => state.productsState.featuredProducts.productIds,
  (state, props) => props.displayFeaturedProducts,
  (state, props) => props.type,
  (
    selectedProductLink,
    products,
    featuredProductIds,
    displayFeaturedProducts,
    type
  ) => {
    for (const id in products) {
      const product = products[id];
      // we only want to filter out products that are already being displayed in the featured widget
      // we need to check for displayFeaturedProducts because featured products may be selected
      // we only want to do this for in stock products as OOS products are filtered out of the featured section
      const productDisplayedInFeatured =
        displayFeaturedProducts &&
        featuredProductIds.includes(id) &&
        type !== 'SOLD_OUT';
      if (
        product.shareUrl === selectedProductLink &&
        !productDisplayedInFeatured
      )
        return id;
    }
    return null;
  }
);

const getSelectedFeaturedProductId: (
  State,
  GetSelectedProductIdProps
) => string | null = createSelector(
  (state: State, props: GetSelectedProductIdProps) => props.selectedProductLink,
  (state: State) => state.productsState.featuredProducts.products,
  (selectedProductLink, products) => {
    for (const id in products) {
      const product = products[id];
      if (product.shareUrl === selectedProductLink) return id;
    }
    return null;
  }
);

export const getSelectedProduct: (
  State,
  {
    +selectedProductLink: string | null,
    +type: string,
    +selectedCategoryId: string | null,
  }
) => BundledProduct | null = createSelector(
  getSelectedProductId,
  makeSelectProducts(),
  (selectedProductId, products) => {
    return products.find((product) => product.id === selectedProductId) || null;
  }
);

export const getSelectedFeaturedProduct: (
  State,
  {
    +selectedProductLink: string | null,
    +type: string,
    +selectedCategoryId: string | null,
  }
) => BundledProduct | null = createSelector(
  getSelectedFeaturedProductId,
  makeSelectFeaturedProducts(),
  (selectedProductId, products) => {
    return products.find((product) => product.id === selectedProductId) || null;
  }
);

const getIndexFromProducts = (viewSize, products, productId) => {
  const ITEMS_PER_ROW = {
    small: 2,
    medium: 3,
  };
  const itemsPerRow = get(ITEMS_PER_ROW, viewSize, 4);
  const indexOfSelectedProduct = products.findIndex(
    (product) => product.id === productId
  );
  if (indexOfSelectedProduct === -1) return null;
  return Math.floor(indexOfSelectedProduct / itemsPerRow) * itemsPerRow;
};

export const getDetailIndex: (
  State,
  {
    ...WithSelectedProductLinkInjectedProps,
    +type: string,
    +selectedCategoryId: string | null,
  }
) => number | null = createSelector(
  makeSelectProducts(),
  getSelectedProductId,
  (state) => state.viewState.viewSize,
  (state, props) => props.accordionInGrid,
  (products, selectedProductId, viewSize, accordionInGrid) => {
    if (!accordionInGrid) return null;
    if (selectedProductId === null) return null;
    return getIndexFromProducts(viewSize, products, selectedProductId);
  }
);

export const getFeaturedProductDetailIndex: (
  State,
  {
    ...WithSelectedProductLinkInjectedProps,
    +type: string,
    +selectedCategoryId: string | null,
  }
) => number | null = createSelector(
  makeSelectFeaturedProducts(),
  getSelectedFeaturedProductId,
  (state) => state.viewState.viewSize,
  (state, props) => props.accordionInGrid,
  (products, selectedProductId, viewSize, accordionInGrid) => {
    if (!accordionInGrid) return null;
    if (selectedProductId === null) return null;
    return getIndexFromProducts(viewSize, products, selectedProductId);
  }
);

export const getTopAccordionProduct: (
  State,
  { ...WithSelectedProductLinkInjectedProps }
) => BundledProduct | null = createSelector(
  (state) => state.productsState.products,
  (state) => state.productsState.variants,
  (state) => state.productsState.favorites,
  (state) => state.cartState.variantIdsBeingUpdated,
  (state) => state.cartState.variantQuantities,
  (state) => state.cartState.variantCadenceTypes,
  (state, props) => props.selectedProductLink,
  (state, props) => props.accordionInGrid,
  (state) => state.cartState.prevPurchasedVariantQuantities,
  (state) => state.productsState.featuredProducts.productIds,
  (state) => state.productsState.subscribableProducts,
  (state, props) => props.optimizelyFeaturedUIDecision.variationName,
  (
    products,
    variants,
    favorites,
    variantIdsBeingUpdated,
    variantQuantities,
    variantCadenceTypes,
    selectedProductLink,
    accordionInGrid,
    prevPurchasedVariantQuantities,
    featuredProductIds,
    subscribableProducts,
    featuredUIVariationName
  ) => {
    if (accordionInGrid) return null;
    let selectedProductId = null;
    for (const id in products) {
      const productIsInFeaturedCarousel =
        featuredUIVariationName === 'carousel' &&
        featuredProductIds.includes(id);
      if (
        products[id].shareUrl === selectedProductLink &&
        !productIsInFeaturedCarousel
      ) {
        selectedProductId = id;
        break;
      }
    }
    if (selectedProductId === null) return null;
    return bundleProduct(
      selectedProductId,
      products,
      variants,
      variantIdsBeingUpdated,
      favorites,
      variantQuantities,
      prevPurchasedVariantQuantities,
      null,
      subscribableProducts,
      variantCadenceTypes
    );
  }
);

export const selectHasFavorites: (State) => boolean = createSelector(
  (state) => state.productsState.favorites,
  (favorites) =>
    Object.values(favorites).filter((isFavorite) => isFavorite).length > 0
);

function bundleProduct(
  productId: string,
  products: { [id: string]: StateProductData },
  variants: { [id: string]: VariantData },
  variantIdsBeingUpdated: Set<string>,
  favorites: Favorites,
  variantQuantities: CartQuantities,
  prevPurchasedVariantQuantities: CartQuantities,
  productType: string,
  subscribableProducts: any,
  variantCadenceTypes: CadenceTypes
): BundledProduct {
  let variantsPrevPurchasedTotal = 0;

  const productVariants = products[productId].variantIds.map((variantId) => {
    const prevPurchasedCount = prevPurchasedVariantQuantities[variantId] || 0;
    const { cartLimit } = variants[variantId];
    const newCartLimit = cartLimit - prevPurchasedCount;
    const isVariantAtPurchaseLimit = newCartLimit === 0;
    const isSubscribableShopVariant = !!subscribableProducts?.[variantId];
    const subscription = subscribableProducts?.[variantId] || {};
    const hasActiveSubscription = !!subscription?.activeSubscription?.quantity;

    if (isVariantAtPurchaseLimit) {
      variantsPrevPurchasedTotal++;
    }

    return {
      ...variants[variantId],
      areCartOperationsDisabled: variantIdsBeingUpdated.has(variantId),
      id: variantId,
      quantityUpdating: variantIdsBeingUpdated.has(variantId), // determines button loading state
      quantity: variantQuantities[variantId] || 0,
      isPreviouslyPurchased: prevPurchasedCount > 0,
      cartLimit: newCartLimit,
      isVariantAtPurchaseLimit,
      subscription,
      isSubscribableShopVariant,
      isSubscriptionAvailable:
        isSubscribableShopVariant && !subscription.soldOut,
      cadenceTypeInCart: variantCadenceTypes?.[variantId] || null,
      hasActiveSubscription,
    };
  });

  const bundledProduct = {
    ...products[productId],
    variants: productVariants,
    id: productId,
    isFavorite: favorites[productId] === true,
    soldOut: productVariants.every((variant) => variant.soldOut),
    isSoldOutOnFetch: productVariants.every(
      (variant) => variant.isSoldOutOnFetch
    ),
    isSubscriptionSoldOutOnFetch: productVariants.every(
      (variant) => variant.subscription.isSoldOutOnFetch
    ),
    isProductAtMaxedCartLimit:
      variantsPrevPurchasedTotal === productVariants.length,
    productType,
    isSubscribableShopProduct: productVariants.some(
      (variant) => variant.isSubscribableShopVariant
    ),
    hasActiveSubscription: !!productVariants.find(
      (variant) => variant.hasActiveSubscription
    ),
  };

  return bundledProduct;
}

function isSomeVariantInCart(product: BundledProduct): boolean {
  return product.variants.some((variant) => variant.quantity > 0);
}

export const selectIsVariantToRemoveSponsored: (State) => boolean = createSelector(
  (state) => state.productsState.variantToRemove,
  (state) => state.productsState.variants,
  (state) => state.productsState.products,
  (variantToRemove, variants, products) => {
    if (variantToRemove === null) return false;
    const productId = variants[variantToRemove].parentId;
    return products[productId].sponsored;
  }
);

export const selectProduct: (
  State,
  { productId: string }
) => StateProductData = createSelector(
  (state, props) => state.productsState.products,
  (state, props) => props.productId,
  (products, productId) => products[productId]
);

export const selectVariant: (
  State,
  { variantId: string }
) => VariantData = createSelector(
  (state, props) => state.productsState.variants,
  (state, props) => props.variantId,
  (variants, variantId) => variants[variantId]
);

export const selectIsProductInStock: (
  State,
  { productId: string }
) => boolean = createSelector(
  selectProduct,
  (state, props) => state.productsState.variants,
  (product, variants) =>
    product.variantIds.some((variantId) => !variants[variantId].soldOut)
);

export const selectProductFromVariantId: (
  State,
  { variantId: string }
) => StateProductData = createSelector(
  selectVariant,
  (state) => state.productsState.products,
  (variant, products) => {
    if (variant) {
      return products[variant.parentId];
    }
    return null;
  }
);

export const selectVariantQuantity: (
  State,
  { variantId: string }
) => number = createSelector(
  (state, props) => props.variantId,
  (state, props) => state.cartState.variantQuantities,
  (variantId, variantQuantities) => variantQuantities[variantId] || 0
);

export const selectProductIds: (State) => Array<string> = createSelector(
  (state) => state.productsState.displayedProductIds,
  (state) => state.productsState.sponsoredProductIds,
  (state) => state.productsState.products,
  (state) => state.productsState.variants,
  (state) => state.searchState.searchResults,
  (state) => state.productsState.featuredProducts.productIds,
  (
    displayedProductIds,
    sponsoredProductIds,
    products,
    variants,
    searchResults,
    featuredProductIds
  ) => {
    const inStockProductIds = [];
    const soldOutProductIds = [];
    const displayedProducts = searchResults || displayedProductIds;
    displayedProducts.forEach((productId) => {
      const product = products[productId];
      if (product) {
        const isProductInStock = product.variantIds.some(
          (variantId) => !variants[variantId].isSoldOutOnFetch
        );
        const isProductSponsored = product.sponsored;
        if (!isProductSponsored) {
          if (isProductInStock) {
            inStockProductIds.push(productId);
          } else {
            soldOutProductIds.push(productId);
          }
        }
      }
    });

    return {
      featuredProducts: featuredProductIds,
      inStock: [...inStockProductIds],
      sponsored: sponsoredProductIds,
      oos: [...soldOutProductIds],
    };
  }
);

export const selectProductRecommendations = createSelector(
  (state) => state.productsState.products,
  (state) => state.productsState.variants,
  (state) => state.productsState.favorites,
  (state) => state.productsState.productRecommendationIds,
  (state) => state.productsState.subscribableProducts,
  (state) => state.cartState.variantIdsBeingUpdated,
  (state) => state.cartState.variantQuantities,
  (state) => state.cartState.prevPurchasedVariantQuantities,
  (state) => state.cartState.variantCadenceTypes,
  (
    products,
    variants,
    favorites,
    productRecommendationIds,
    subscribableProducts,
    variantIdsBeingUpdated,
    variantQuantities,
    prevPurchasedVariantQuantities,
    variantCadenceTypes
  ) => {
    return productRecommendationIds
      .map((productId) =>
        bundleProduct(
          productId,
          products,
          variants,
          variantIdsBeingUpdated,
          favorites,
          variantQuantities,
          prevPurchasedVariantQuantities,
          productTypeEnum.IN_STOCK,
          subscribableProducts,
          variantCadenceTypes
        )
      )
      .filter((product) => {
        const { soldOut } = product;
        // filter out OOS products
        return !soldOut;
      })
      .slice(0, 6); // MAX = 6
  }
);
